import { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import SubmitButton from '../AuthPages/SubmitButton';
import Spinner from '../../components/Spinner';
import { Context } from '../../AuthContext';
import {
  getPersonalVerticalsLimit,
  getJoinedVerticalsLimit,
  getInvitedUsersLimit,
} from '../../config/planLimits';

const PERSONAL_VERTICAL_LIMIT = getPersonalVerticalsLimit('free');
const JOINED_VERTICAL_LIMIT = getJoinedVerticalsLimit('free');
const INVITED_USERS_LIMIT = getInvitedUsersLimit('free');

const formatDowngradedPermission = (p) => {
  // Explorer accounts cannot hold any editor role on a Joined Vertical.
  if (p === 'AdvancedEditor' || p === 'Editor' || p === 'Member') return 'Member';
  return p || '—';
};

const ConfirmDowngradePage = () => {
  const { user } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const initialSelectedIds = location.state?.selectedIds;
  const initialSelectedJoinedIds = location.state?.selectedJoinedIds;
  const initialMyVerticalUserConfig = location.state?.myVerticalUserConfig;

  const [verticals, setVerticals] = useState([]);
  const [joinedVerticals, setJoinedVerticals] = useState([]);
  const [loadingVerticals, setLoadingVerticals] = useState(true);
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(Array.isArray(initialSelectedIds) ? initialSelectedIds : [])
  );
  const [selectedJoinedIds, setSelectedJoinedIds] = useState(
    () => new Set(Array.isArray(initialSelectedJoinedIds) ? initialSelectedJoinedIds : [])
  );
  const [downloadingId, setDownloadingId] = useState(null);
  const [usersByVertical, setUsersByVertical] = useState({});
  const [userConfigByVertical, setUserConfigByVertical] = useState(() => {
    if (!initialMyVerticalUserConfig) return {};
    return Object.fromEntries(
      Object.entries(initialMyVerticalUserConfig).map(([vid, cfg]) => [
        vid,
        {
          keptEmails: new Set(cfg.keptEmails || []),
          basicEditorEmails: new Set(cfg.basicEditorEmails || []),
        },
      ])
    );
  });

  useEffect(() => {
    document.title = 'Confirm Downgrade - The Simple Platform';
  }, []);

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;

    const fetchVerticals = async () => {
      try {
        const token = await user.getIdToken();
        const [createdVerticalsRes, joinedVerticalRes] = await Promise.all([
          fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'tasks', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'get_vertical_names', {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const createdVerticalsData = createdVerticalsRes.ok ? await createdVerticalsRes.json() : [];
        const joinedVerticalData = joinedVerticalRes.ok ? await joinedVerticalRes.json() : [];
        if (!cancelled) {
          setVerticals(Array.isArray(createdVerticalsData) ? createdVerticalsData : []);
          const joined = Array.isArray(joinedVerticalData)
            ? joinedVerticalData.filter((v) => v.status === 'Joined')
            : [];
          setJoinedVerticals(joined);
        }

        // For each My Vertical (Created with collaborators), fetch the user list
        const myVerticalsList = (
          Array.isArray(createdVerticalsData) ? createdVerticalsData : []
        ).filter(
          (v) => (v.status === 'Pending' || v.status === 'Completed') && v.invited_count > 0
        );
        const userListResults = await Promise.all(
          myVerticalsList.map((v) =>
            fetch(
              `${process.env.REACT_APP_FLASK_WEBSERVER}getUsersAndInvitiations?vertical_id=${v._id}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
              .then((r) => (r.ok ? r.json() : []))
              .then((data) => [v._id, Array.isArray(data) ? data : []])
              .catch(() => [v._id, []])
          )
        );
        if (!cancelled) {
          const map = Object.fromEntries(userListResults);
          // Filter the current user (Creator) out of each list — they can't disinvite themselves
          const filtered = Object.fromEntries(
            Object.entries(map).map(([vid, list]) => [
              vid,
              list.filter((u) => u.email !== user?.email),
            ])
          );
          setUsersByVertical(filtered);
          // Initialize empty config for each vertical (so user opts-in)
          setUserConfigByVertical((prev) => {
            const next = { ...prev };
            for (const vid of Object.keys(filtered)) {
              if (!next[vid]) {
                next[vid] = { keptEmails: new Set(), basicEditorEmails: new Set() };
              }
            }
            return next;
          });
        }
      } catch (e) {
        console.error('Failed to fetch verticals', e);
        if (!cancelled) {
          setVerticals([]);
          setJoinedVerticals([]);
        }
      } finally {
        if (!cancelled) setLoadingVerticals(false);
      }
    };

    fetchVerticals();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < PERSONAL_VERTICAL_LIMIT) {
        next.add(id);
      }
      return next;
    });
  };

  const toggleJoinedSelect = (id) => {
    setSelectedJoinedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < JOINED_VERTICAL_LIMIT) {
        next.add(id);
      }
      return next;
    });
  };

  const toggleKeepUser = (verticalId, email) => {
    setUserConfigByVertical((prev) => {
      const cfg = prev[verticalId] || {
        keptEmails: new Set(),
        basicEditorEmails: new Set(),
      };
      const nextKept = new Set(cfg.keptEmails);
      const nextBasic = new Set(cfg.basicEditorEmails);
      if (nextKept.has(email)) {
        nextKept.delete(email);
        nextBasic.delete(email);
      } else if (nextKept.size < INVITED_USERS_LIMIT) {
        nextKept.add(email);
      }
      return {
        ...prev,
        [verticalId]: { keptEmails: nextKept, basicEditorEmails: nextBasic },
      };
    });
  };

  const downloadVertical = async (vertical) => {
    setDownloadingId(vertical._id);
    try {
      const token = await user.getIdToken();
      const url = `${process.env.REACT_APP_FLASK_WEBSERVER}download_vertical?vertical_id=${vertical._id}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${vertical.query}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloadingId(null);
  };

  const personalVerticals = verticals.filter(
    (v) => v.status === 'Pending' || v.status === 'Completed'
  );
  const draftVerticals = personalVerticals.filter((v) => v.invited_count === 0);
  const myVerticals = personalVerticals.filter((v) => v.invited_count > 0);
  const selectedCount = selectedIds.size;
  const limitReached = selectedCount >= PERSONAL_VERTICAL_LIMIT;

  const joinedSelectedCount = selectedJoinedIds.size;
  const joinedLimitReached = joinedSelectedCount >= JOINED_VERTICAL_LIMIT;

  const handleReview = () => {
    const selectedVerticals = personalVerticals.filter((v) => selectedIds.has(v._id));
    const unselectedVerticals = personalVerticals.filter((v) => !selectedIds.has(v._id));
    const selectedJoinedVerticals = joinedVerticals.filter((v) => selectedJoinedIds.has(v.id));
    const unselectedJoinedVerticals = joinedVerticals.filter((v) => !selectedJoinedIds.has(v.id));
    const myVerticalUserConfig = Object.fromEntries(
      Object.entries(userConfigByVertical).map(([vid, cfg]) => [
        vid,
        {
          keptEmails: Array.from(cfg.keptEmails),
          basicEditorEmails: Array.from(cfg.basicEditorEmails),
        },
      ])
    );
    navigate('/review-downgrade', {
      state: {
        selectedIds: Array.from(selectedIds),
        selectedVerticals,
        unselectedVerticals,
        selectedJoinedIds: Array.from(selectedJoinedIds),
        selectedJoinedVerticals,
        unselectedJoinedVerticals,
        myVerticalUserConfig,
        usersByVertical,
      },
    });
  };

  return (
    <div style={{ textAlign: 'left' }}>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          navigate(-1);
        }}
      >
        &lt; Go Back
      </a>{' '}
      <h2>Confirm Downgrade to Explorer</h2>
      <p>
        Before downgrading, you'll need to make a few selections to align your account with Explorer
        limits.
        <br />
        Any content you choose not to keep will be permanently removed from the platform. You may
        download any Vertical before completing this process.
      </p>
      <div style={{ marginLeft: '16px' }}>
        <h3 style={{ color: '#356AC3', fontSize: '18px', marginBottom: 0 }}>
          Personal Verticals Selected to Keep: {selectedCount} of {PERSONAL_VERTICAL_LIMIT}
        </h3>
        {loadingVerticals ? (
          <Spinner />
        ) : (
          <>
            <VerticalSection
              title="Private Verticals"
              description={`Explorer accounts may keep up to ${PERSONAL_VERTICAL_LIMIT} Personal Verticals total (Private + My Verticals combined). Select which Private Verticals you would like to retain.\nPrivate Verticals not selected will be permanently deleted. You may download any Vertical before finalizing your downgrade.`}
              columnHeader="Private Vertical Name"
              items={draftVerticals}
              selectedIds={selectedIds}
              limitReached={limitReached}
              onToggle={toggleSelect}
              onDownload={downloadVertical}
              downloadingId={downloadingId}
            />

            <VerticalSection
              title="My Verticals"
              description={`Explorer accounts may keep up to ${PERSONAL_VERTICAL_LIMIT} Personal Verticals total (Private + My Verticals combined). Select which My Verticals you would like to retain.\nFor retained My Verticals:\n- Invited Users must not exceed 10 per Vertical.\n- All collaborators are currently shown as Members to comply with Explorer plan limits.\n- You may assign up to 2 users as Basic Editors per Vertical.\nAny My Verticals not selected will be permanently deleted. You may download any Vertical before finalizing your downgrade.`}
              columnHeader="Vertical Name"
              items={myVerticals}
              selectedIds={selectedIds}
              limitReached={limitReached}
              onToggle={toggleSelect}
              onDownload={downloadVertical}
              downloadingId={downloadingId}
              renderUnderRow={(vertical) => (
                <MyVerticalUserManagement
                  verticalId={vertical._id}
                  users={usersByVertical[vertical._id] || []}
                  config={
                    userConfigByVertical[vertical._id] || {
                      keptEmails: new Set(),
                      basicEditorEmails: new Set(),
                    }
                  }
                  disabled={!selectedIds.has(vertical._id)}
                  onToggleKeep={(email) => toggleKeepUser(vertical._id, email)}
                />
              )}
            />

            <h3 style={{ color: '#356AC3', fontSize: '18px', marginBottom: 0 }}>
              Joined Verticals Selected to Stay In: {joinedSelectedCount} of {JOINED_VERTICAL_LIMIT}
            </h3>
            <JoinedVerticalSection
              title="Joined Verticals"
              description={`Explorer accounts may participate in up to ${JOINED_VERTICAL_LIMIT} Joined Verticals. Select which Verticals you would like to remain a part of.\nIf you are currently an Advanced Editor on any retained Vertical, your role will be changed to Basic Editor upon downgrade.\nYou will be removed from any Joined Verticals not selected.`}
              items={joinedVerticals}
              selectedIds={selectedJoinedIds}
              limitReached={joinedLimitReached}
              onToggle={toggleJoinedSelect}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '10px',
              }}
            >
              <SubmitButton text="Review Choices" width="240px" onClick={handleReview} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const VerticalSection = ({
  title,
  description,
  columnHeader,
  items,
  selectedIds,
  limitReached,
  onToggle,
  onDownload,
  downloadingId,
  renderUnderRow,
}) => (
  <div>
    <h3 style={{ fontSize: '24px', marginBottom: 0 }}>{title}</h3>
    <p style={{ whiteSpace: 'pre-line', marginTop: 0, color: '#444' }}>{description}</p>

    {items.length === 0 ? (
      <p style={{ color: '#888' }}>No {title.toLowerCase()} to display.</p>
    ) : (
      <div>
        <div style={headerRowStyle}>
          <span style={{ color: '#666', paddingLeft: '28px', flex: 1 }}>{columnHeader}</span>
          <span style={downloadColStyle}>Download</span>
        </div>
        {items.map((vertical) => {
          const checked = selectedIds.has(vertical._id);
          const disabled = !checked && limitReached;
          return (
            <div key={vertical._id}>
              <div style={itemRowStyle}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    flex: 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onToggle(vertical._id)}
                  />
                  <span>{vertical.query}</span>
                </label>
                <div style={downloadColStyle}>
                  {downloadingId === vertical._id ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <DownloadOutlinedIcon
                      style={{ cursor: 'pointer' }}
                      onClick={() => onDownload(vertical)}
                    />
                  )}
                </div>
              </div>
              {renderUnderRow ? renderUnderRow(vertical) : null}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const JoinedVerticalSection = ({
  title,
  description,
  items,
  selectedIds,
  limitReached,
  onToggle,
}) => (
  <div>
    <h3 style={{ fontSize: '24px', marginBottom: 0 }}>{title}</h3>
    <p style={{ whiteSpace: 'pre-line', marginTop: 0, color: '#444' }}>{description}</p>

    {items.length === 0 ? (
      <p style={{ color: '#888' }}>No joined verticals to display.</p>
    ) : (
      <div>
        <div style={headerRowStyle}>
          <span style={{ color: '#666', paddingLeft: '28px', flex: 1 }}>Joined Vertical Name</span>
          <span style={{ ...joinedColStyle, color: '#666' }}>Creator</span>
          <span style={{ ...joinedColStyle, color: '#666' }}>Permission</span>
        </div>
        {items.map((vertical) => {
          const checked = selectedIds.has(vertical.id);
          const disabled = !checked && limitReached;
          return (
            <div key={vertical.id} style={itemRowStyle}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  flex: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggle(vertical.id)}
                />
                <span>{vertical.name}</span>
              </label>
              <div style={joinedColStyle}>{vertical.creator_name || '—'}</div>
              <div style={joinedColStyle}>{formatDowngradedPermission(vertical.permission)}</div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const headerRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 4px',
  borderBottom: '1px solid #ddd',
};

const itemRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 4px',
  borderBottom: '1px solid #eee',
};

const downloadColStyle = {
  width: '100px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '#666',
};

const joinedColStyle = {
  width: '180px',
  display: 'flex',
  alignItems: 'center',
  color: '#444',
};

const MyVerticalUserManagement = ({ verticalId, users, config, disabled, onToggleKeep }) => {
  if (users.length === 0) {
    return (
      <div
        style={{
          margin: '8px 0 16px 32px',
          color: '#888',
          fontSize: '13px',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        No collaborators on this Vertical.
      </div>
    );
  }

  const memberCount = config.keptEmails.size;
  const inviteCapHit = config.keptEmails.size >= INVITED_USERS_LIMIT;

  return (
    <div
      style={{
        margin: '8px 0 24px 32px',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <p style={{ margin: 0, color: '#356AC3', fontWeight: 700, fontSize: '14px' }}>
        Total Number of Members Selected: {memberCount} of {INVITED_USERS_LIMIT}
      </p>
      <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'gray', lineHeight: 1.4 }}>
        Editor permissions are only available on Full Access. Every collaborator you keep will
        become a Member.
      </p>

      <div style={userHeaderRowStyle}>
        <span style={{ width: '32px' }}></span>
        <span style={{ flex: 2, color: '#666' }}>Full Name</span>
        <span style={{ flex: 3, color: '#666' }}>Email</span>
        <span style={{ flex: 1, color: '#666' }}>Permission</span>
      </div>
      {users.map((u) => {
        const kept = config.keptEmails.has(u.email);
        const checkboxDisabled = !kept && inviteCapHit;
        return (
          <div key={u.email} style={userRowStyle}>
            <div style={{ width: '32px', display: 'flex', justifyContent: 'center' }}>
              <input
                type="checkbox"
                checked={kept}
                disabled={checkboxDisabled}
                onChange={() => onToggleKeep(u.email)}
              />
            </div>
            <span style={{ flex: 2 }}>{u.name}</span>
            <span style={{ flex: 3 }}>{u.email}</span>
            <span
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                color: kept ? '#344054' : '#888',
              }}
            >
              Member
            </span>
          </div>
        );
      })}
    </div>
  );
};

const userHeaderRowStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '6px 4px',
  borderBottom: '1px solid #ddd',
  fontSize: '13px',
};

const userRowStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 4px',
  borderBottom: '1px solid #eee',
  fontSize: '14px',
};

export default ConfirmDowngradePage;
